// @flow

import * as React from 'react';
import {TooltipContainer} from 'sail/Tooltip';

import Icon from 'sail/Icon';
import {lock} from 'sail/Icon/Assets';

import Box from 'sail/Box';
import {Body} from 'sail/Text';
import type {
  NavigationXRay,
  ConditionGroupXRayData,
  ConditionXRayData,
} from 'docs/data';

import './SidebarInfoTooltip.scss';
import {defineMessages, FormattedMessage} from 'react-intl';
import List, {ListItem} from 'sail/List';

const messages = defineMessages({
  affirmativeHeader: {
    id: 'docsShell.sidebarNavigation.sidebarLinkTooltip.affirmativeHeader',
    description: 'A heading above a list of conditions that must be true',
    defaultMessage:
      'This link and any children will appear in the sidebar when:',
  },
  negativeHeader: {
    id: 'docsShell.sidebarNavigation.sidebarLinkTooltip.negativeHeader',
    description: 'A heading above a list of conditions that must be false',
    defaultMessage:
      'This link and any children will appear in the sidebar unless:',
  },
});

type ConditionsListsProps = {
  conditions: ConditionGroupXRayData,
};

const ConditionsLists = ({conditions}: ConditionsListsProps) => {
  const {affirmative, negative} = conditions;
  return (
    <>
      {affirmative.length ? (
        <ConditionList conditions={affirmative} affirmative />
      ) : null}
      {negative.length ? <ConditionList conditions={negative} /> : null}
    </>
  );
};

type ConditionListProps = {
  conditions: Array<ConditionXRayData>,
  affirmative?: boolean,
};

const ConditionList = ({
  conditions,
  affirmative = false,
}: ConditionListProps) => {
  const message = affirmative
    ? messages.affirmativeHeader
    : messages.negativeHeader;
  return (
    <Box>
      <FormattedMessage {...message} />
      <List className="SidebarInfoTooltip-list" type="bulleted" size="medium">
        {conditions.map((n) => (
          <ListItem key={`condition-${n.name}`}>
            <Body>{`${n.name} ${
              ['gate', 'flag'].includes(n.type) ? `${n.type} is true` : ''
            }`}</Body>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

type SidebarInfoTooltipProps = {
  xray?: ?NavigationXRay,
};

export const SidebarInfoTooltip = ({
  xray,
}: SidebarInfoTooltipProps): React.Node => {
  if (!xray?.conditions) {
    return null;
  }
  return (
    <TooltipContainer
      className="SidebarInfoTooltip"
      context={<Icon icon={lock} size={12} color="blue" />}
      contentMaxWidth={500}
      tooltipContents={<ConditionsLists conditions={xray?.conditions} />}
      position="right"
    />
  );
};
